/**
 * Image Utilities for Google Docs-Style Image Support
 * Handles cursor-based insertion, measurement, and manipulation
 */

// Metadata interface matching Swift ImageMetadata
interface ImageMetadata {
  latitude?: number;
  longitude?: number;
  address?: string;
  capturedAt: Date;
}

// Address parsing result (matching Swift AddressComponents)
interface AddressComponents {
  street: string;
  cityState: string;
  zip: string;
}

/**
 * Insert an image node at the current cursor position
 * Preserves selection and maintains proper DOM structure
 */
export const insertImageAtCursor = (
  imageUrl: string, 
  defaultWidth: number = 133, 
  metadata?: ImageMetadata
): boolean => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    console.warn('No selection available for image insertion');
    return false;
  }

  const range = selection.getRangeAt(0);
  
  // Create the image element (simplified approach like Google Docs)
  const img = document.createElement('img');
  img.src = imageUrl;
  img.crossOrigin = 'anonymous'; // For PDF export compatibility
  img.className = 'inline-image';
  // DON'T set contentEditable="false" - causes range calculation issues
  img.style.cssText = `
    display: inline-block;
    position: relative;
    margin: 0;
    max-width: 100%;
    width: ${defaultWidth}px;
    height: auto;
    cursor: pointer;
    vertical-align: top;
    border: 2px solid transparent;
    transition: border-color 0.2s ease;
  `;
  img.draggable = false;

  // Add data attributes for tracking
  img.setAttribute('data-original-width', defaultWidth.toString());
  img.setAttribute('data-image-url', imageUrl);
  img.setAttribute('data-image-id', 'img-' + Date.now());
  
  // Set default overlay scale
  img.setAttribute('data-overlay-scale', '1.0');

  // Store metadata as data attributes if provided
  if (metadata) {
    img.setAttribute('data-metadata-lat', metadata.latitude?.toString() || '');
    img.setAttribute('data-metadata-lng', metadata.longitude?.toString() || '');
    img.setAttribute('data-metadata-address', metadata.address || '');
    
    // Safely handle capturedAt date
    let capturedAtString = '';
    if (metadata.capturedAt) {
      try {
        const dateObj = metadata.capturedAt instanceof Date ? metadata.capturedAt : new Date(metadata.capturedAt);
        if (!isNaN(dateObj.getTime())) {
          capturedAtString = dateObj.toISOString();
        } else {
          capturedAtString = new Date().toISOString(); // Fallback to current date
        }
      } catch (error) {
        console.warn('Invalid capturedAt date, using current date:', error);
        capturedAtString = new Date().toISOString();
      }
    } else {
      capturedAtString = new Date().toISOString(); // Fallback to current date
    }
    
    img.setAttribute('data-metadata-captured', capturedAtString);
    
    // 🎯 SMART DEFAULT: Only show overlay if image has valid address metadata
    const hasValidMetadata = metadata.address && 
      !metadata.address.startsWith('Location:') && 
      metadata.address !== 'Location Unavailable' &&
      metadata.address.trim().length > 0;
    
    img.setAttribute('data-metadata-visible', hasValidMetadata ? 'true' : 'false');
  }

  // Create minimal container for React portal system
  const container = createSimpleImageContainer(img, metadata || null);
  
  // 🎯 ADD FLOAT CLASS FOR GOOGLE DOCS-STYLE WRAPPING
  // Default to float-left for text wrapping behavior
  container.classList.add('image-float-left');
  
  // Delete any selected content first
  range.deleteContents();
  
  // CRITICAL FIX: Ensure we're inside a paragraph for proper float wrapping
  let insertionPoint = range.commonAncestorContainer;
  
  // If we're in a text node, get its parent
  if (insertionPoint.nodeType === Node.TEXT_NODE) {
    insertionPoint = insertionPoint.parentNode as HTMLElement;
  }
  
  // Check if we're in a paragraph
  const parentParagraph = (insertionPoint as HTMLElement).closest('p');
  
  if (!parentParagraph) {
    // Not in a paragraph - wrap in one
    const p = document.createElement('p');
    p.style.margin = '0';
    p.appendChild(container);
    
    // CRITICAL: Add text node after image for wrapping to work
    const textNode = document.createTextNode('\u200B'); // Zero-width space for cursor
    p.appendChild(textNode);
    
    range.insertNode(p);
    
    // Position cursor in the text node so typing flows beside image
    range.setStart(textNode, 0);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } else {
    // Inside paragraph - insert at cursor position
    range.insertNode(container);
    
    // CRITICAL: Ensure there's a text node after image for wrapping
    // Check if there's already content after
    const nextSibling = container.nextSibling;
    
    if (!nextSibling || (nextSibling.nodeType === Node.ELEMENT_NODE && (nextSibling as HTMLElement).classList.contains('inline-image-container'))) {
      // No text after image or another image - add text node
      const textNode = document.createTextNode('\u200B');
      container.parentNode!.insertBefore(textNode, container.nextSibling);
      
      // Position cursor in text node
      range.setStart(textNode, 0);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // There's content after - position cursor there
      if (nextSibling.nodeType === Node.TEXT_NODE) {
        range.setStart(nextSibling, 0);
      } else {
        range.setStartAfter(container);
      }
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
  
  // Focus the editor so user can click to position cursor
  const editorRoot = img.closest('[contenteditable="true"]');
  if (editorRoot) {
    (editorRoot as HTMLElement).focus();
  }

  // Set up right-click context menu for the image
  setupImageContextMenu(img);

  return true;
};

/**
 * Show resize handles for a selected image (Google Docs style)
 */
export const showResizeHandles = (img: HTMLImageElement): void => {
  // Remove any existing selection borders
  hideResizeHandles();

  // Add selection to this image
  img.classList.add('selected');
  img.style.border = '2px solid #1a73e8';
  
  // Create floating resize handles container
  const handlesContainer = document.createElement('div');
  handlesContainer.className = 'image-resize-handles';
  handlesContainer.setAttribute('data-for-image', img.getAttribute('data-image-id') || '');
  
  // Position the container over the image container (not just the image)
  const container = img.closest('.inline-image-container') as HTMLElement;
  const targetElement = container || img;
  const rect = targetElement.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  
  // Adjust for floated images
  const isFloated = container && (
    container.classList.contains('image-float-left') || 
    container.classList.contains('image-float-right')
  );
  
  let topPos = rect.top + scrollTop - 4;
  let leftPos = rect.left + scrollLeft - 4;
  
  if (isFloated && container) {
    // Account for float margins in positioning
    const computedStyle = window.getComputedStyle(container);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    topPos -= marginTop;
    leftPos -= marginLeft;
  }
  
  handlesContainer.style.cssText = `
    position: absolute;
    top: ${topPos}px;
    left: ${leftPos}px;
    width: ${rect.width + 8}px;
    height: ${rect.height + 8}px;
    pointer-events: none;
    z-index: 1000;
  `;
  
  // Add scroll listener to keep handles in sync with image position
  let scrollRAF: number | null = null;
  const scrollListener = () => {
    if (scrollRAF) {
      cancelAnimationFrame(scrollRAF);
    }
    scrollRAF = requestAnimationFrame(() => {
      updateHandlePositions(img);
      scrollRAF = null;
    });
  };
  
  // Store scroll listener and RAF reference on the container for cleanup
  (handlesContainer as any).__scrollListener = scrollListener;
  (handlesContainer as any).__scrollRAF = () => scrollRAF;
  window.addEventListener('scroll', scrollListener);
  document.addEventListener('scroll', scrollListener, true); // Capture phase for nested scrolling
  
  // Create 8 resize handles
  const handles = [
    { class: 'nw', cursor: 'nw-resize', top: '0', left: '0' },
    { class: 'n', cursor: 'n-resize', top: '0', left: '50%', transform: 'translateX(-50%)' },
    { class: 'ne', cursor: 'ne-resize', top: '0', right: '0' },
    { class: 'e', cursor: 'e-resize', top: '50%', right: '0', transform: 'translateY(-50%)' },
    { class: 'se', cursor: 'se-resize', bottom: '0', right: '0' },
    { class: 's', cursor: 's-resize', bottom: '0', left: '50%', transform: 'translateX(-50%)' },
    { class: 'sw', cursor: 'sw-resize', bottom: '0', left: '0' },
    { class: 'w', cursor: 'w-resize', top: '50%', left: '0', transform: 'translateY(-50%)' }
  ];

  handles.forEach(handle => {
    const handleEl = document.createElement('div');
    handleEl.className = `resize-handle resize-handle-${handle.class}`;
    handleEl.style.cssText = `
      position: absolute;
      width: 8px;
      height: 8px;
      background: #1a73e8;
      border: 1px solid white;
      border-radius: 50%;
      cursor: ${handle.cursor};
      pointer-events: auto;
      z-index: 1001;
      ${handle.top ? `top: ${handle.top};` : ''}
      ${handle.bottom ? `bottom: ${handle.bottom};` : ''}
      ${handle.left ? `left: ${handle.left};` : ''}
      ${handle.right ? `right: ${handle.right};` : ''}
      ${handle.transform ? `transform: ${handle.transform};` : ''}
    `;
    
    handleEl.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      startResize(img, handle.class, e);
    });
    
    handlesContainer.appendChild(handleEl);
  });
  
  // Add to document body for absolute positioning
  document.body.appendChild(handlesContainer);
  
  // Also add resize observer to handle window resizing
  const resizeObserver = new ResizeObserver(() => {
    updateHandlePositions(img);
  });
  resizeObserver.observe(img);
  
  // Store resize observer for cleanup
  (handlesContainer as any).__resizeObserver = resizeObserver;
  

};

/**
 * Hide all resize handles
 */
export const hideResizeHandles = (): void => {
  // Remove selection from all inline images
  document.querySelectorAll('.inline-image.selected').forEach(img => {
    img.classList.remove('selected');
    (img as HTMLImageElement).style.border = '2px solid transparent';
  });
  
  // Remove all floating handle containers and cleanup scroll listeners
  document.querySelectorAll('.image-resize-handles').forEach(handles => {
    // Clean up scroll listener
    const scrollListener = (handles as any).__scrollListener;
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener);
      document.removeEventListener('scroll', scrollListener, true);
    }
    
    // Clean up RAF if pending
    const getRAF = (handles as any).__scrollRAF;
    if (getRAF) {
      const rafId = getRAF();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    }
    
    // Clean up resize observer
    const resizeObserver = (handles as any).__resizeObserver;
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    
    handles.remove();
  });
};

/**
 * Start resize operation for an inline image
 */
const startResize = (img: HTMLImageElement, handleClass: string, e: MouseEvent): void => {
  e.preventDefault();
  e.stopPropagation();
  
  const startX = e.clientX;
  const startY = e.clientY;
  const startWidth = img.offsetWidth;
  const startHeight = img.offsetHeight;
  const aspectRatio = startHeight / startWidth;
  

  
  const handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();
    
    let newWidth = startWidth;
    let newHeight = startHeight;
    
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;
    
    // Calculate new dimensions based on handle
    switch (handleClass) {
      case 'se': // Southeast - grow both directions
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'sw': // Southwest - grow width left, height down
        newWidth = Math.max(50, startWidth - deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'ne': // Northeast - grow width right, height up
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'nw': // Northwest - grow both directions left/up
        newWidth = Math.max(50, startWidth - deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'e': // East - grow width only
        newWidth = Math.max(50, startWidth + deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'w': // West - grow width left
        newWidth = Math.max(50, startWidth - deltaX);
        newHeight = newWidth * aspectRatio;
        break;
      case 'n': // North - maintain aspect ratio
        newHeight = Math.max(30, startHeight - deltaY);
        newWidth = newHeight / aspectRatio;
        break;
      case 's': // South - maintain aspect ratio
        newHeight = Math.max(30, startHeight + deltaY);
        newWidth = newHeight / aspectRatio;
        break;
    }
    
    // Cap maximum size
    newWidth = Math.min(newWidth, 800);
    newHeight = Math.min(newHeight, 600);
    
    // Apply new size
    img.style.width = newWidth + 'px';
    img.style.height = newHeight + 'px';
    
    // Update overlay scale factor based on new width
    updateOverlayScale(img, newWidth);
    
    // Update resize handles position
    updateHandlePositions(img);
    
    // Update ALL viewport overlays since resizing may push other images around
    updateAllViewportOverlays();
    
    // CSS handles overlay scaling automatically via CSS custom properties
  };
  
  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    // Dispatch custom event to trigger overlay position updates
    document.dispatchEvent(new CustomEvent('imageResized', { 
      detail: { imageId: img.getAttribute('data-image-id') } 
    }));
    //console.log('📏 Image resize completed, dispatching imageResized event');
  };
  
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};

/**
 * Update the position of resize handles to match image container
 */
const updateHandlePositions = (img: HTMLImageElement): void => {
  const handleContainer = document.querySelector(`.image-resize-handles[data-for-image="${img.getAttribute('data-image-id')}"]`) as HTMLElement;
  if (!handleContainer) return;
  
  // Use the image container if available, otherwise fall back to the image
  const container = img.closest('.inline-image-container') as HTMLElement;
  const targetElement = container || img;
  const rect = targetElement.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  
  // Adjust for floated images
  const isFloated = container && (
    container.classList.contains('image-float-left') || 
    container.classList.contains('image-float-right')
  );
  
  let topPos = rect.top + scrollTop - 4;
  let leftPos = rect.left + scrollLeft - 4;
  
  if (isFloated && container) {
    // Account for float margins in positioning
    const computedStyle = window.getComputedStyle(container);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    topPos -= marginTop;
    leftPos -= marginLeft;
  }
  
  handleContainer.style.top = topPos + 'px';
  handleContainer.style.left = leftPos + 'px';
  handleContainer.style.width = (rect.width + 8) + 'px';
  handleContainer.style.height = (rect.height + 8) + 'px';
};

/**
 * Initialize resize behavior for an image (legacy compatibility)
 */
export const initializeImageResize = (
  figure: HTMLElement,
  onResizeStart?: () => void,
  onResizeEnd?: () => void
): void => {
  // This function is kept for compatibility but simplified for inline images
};

/**
 * Check if an element is an image element
 */
export const isImageElement = (element: HTMLElement): element is HTMLImageElement => {
  return element.tagName === 'IMG' && element.classList.contains('inline-image');
};

/**
 * Check if an element is an image container
 */
export const isImageContainer = (element: HTMLElement): boolean => {
  return element.classList.contains('inline-image-container');
};

/**
 * Get image element height for pagination
 */
export const getImageElementHeight = (element: HTMLElement): number => {
  if (isImageElement(element)) {
    return element.offsetHeight + 8; // Add some margin
  }
  return 0;
};

/**
 * Measure image height for pagination calculations
 */
export const measureImageHeight = (imageElement: HTMLElement): number => {
  if (isImageElement(imageElement)) {
    return imageElement.offsetHeight + 8;
  }
  return 0;
};

/**
 * Update overlay scale factor based on image width
 * Sets CSS custom property for proportional scaling
 */
const updateOverlayScale = (img: HTMLImageElement, newWidth: number): void => {
  const container = img.closest('.inline-image-container') as HTMLElement;
  if (!container) return;
  
  const overlay = container.querySelector('.image-metadata-overlay') as HTMLElement;
  if (!overlay) return;
  
  // Set the CSS custom property for scaling
  overlay.style.setProperty('--image-width', newWidth.toString());
};

/**
 * Initialize overlay scale for an image container
 * Called when image is first inserted or after DOM reconstruction
 */
const initializeOverlayScale = (container: HTMLElement): void => {
  const img = container.querySelector('.inline-image') as HTMLImageElement;
  const overlay = container.querySelector('.image-metadata-overlay') as HTMLElement;
  
  if (!img || !overlay) return;
  
  const currentWidth = img.offsetWidth || parseInt(img.style.width) || 133;
  overlay.style.setProperty('--image-width', currentWidth.toString());
};

/**
 * Handle metadata overlay creation with smart address upgrade
 * Implements the same logic as iOS: check address, upgrade from coordinates if needed, only show overlay if real address exists
 */
const handleMetadataOverlayCreation = async (container: HTMLElement, img: HTMLImageElement, metadata: ImageMetadata): Promise<void> => {
  try {
    let finalAddress = metadata.address;
    let shouldCreateOverlay = false;
    
    // Step 1: Check if we already have a real street address (not coordinates)
    if (metadata.address && 
        metadata.address.trim() !== '' && 
        !metadata.address.startsWith('Location: ')) {
      shouldCreateOverlay = true;
    }
    // Step 2: If no real address but we have coordinates, attempt smart upgrade
    else if ((!metadata.address || 
              metadata.address.trim() === '' || 
              metadata.address.startsWith('Location: ')) &&
             metadata.latitude && 
             metadata.longitude) {
      
      try {
        const upgradedAddress = await fetchAddressFromCoordinates(metadata.latitude, metadata.longitude);
        if (upgradedAddress) {
          finalAddress = upgradedAddress;
          shouldCreateOverlay = true;
          
          // Update the image data attributes with the new address
          img.setAttribute('data-metadata-address', upgradedAddress);
        } else {
          shouldCreateOverlay = false;
        }
      } catch (error) {
        console.error('Address upgrade failed:', error);
        shouldCreateOverlay = false;
      }
    }
    // Step 3: No address and no coordinates - no overlay
    else {
      shouldCreateOverlay = false;
    }
    
    // OLD HTML overlays disabled - using React Portal system instead
    // Create overlay only if we have a real address
    // if (shouldCreateOverlay && finalAddress) {
    //   // Create updated metadata object
    //   const updatedMetadata: ImageMetadata = {
    //     ...metadata,
    //     address: finalAddress
    //   };
    //   
    //   const overlay = createMetadataOverlay(updatedMetadata);
    //   container.appendChild(overlay);
    //   
    //   // Initialize overlay scale
    //   initializeOverlayScale(container);
    // }
    
  } catch (error) {
    console.error('Error in handleMetadataOverlayCreation:', error);
  }
};

/**
 * Step 1: Create minimal image container for React portal system
 * This container is atomic to contentEditable but provides overlay linking
 */
const createSimpleImageContainer = (img: HTMLImageElement, metadata: ImageMetadata | null): HTMLElement => {
  // Generate unique overlay ID for React portal linking
  const overlayId = img.getAttribute('data-image-id') || `overlay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  img.setAttribute('data-image-id', overlayId);

  // Create minimal container - MUST be contentEditable="false" for atomic behavior
  const container = document.createElement('span');
  container.className = 'inline-image-container';
  container.contentEditable = 'false'; // Critical: prevents contentEditable conflicts
  container.setAttribute('data-overlay-id', overlayId);
  
  // Store metadata as JSON for React portal system
  if (metadata) {
    // 🎯 SMART DEFAULT: Check if metadata has valid address
    const hasValidMetadata = metadata.address && 
      !metadata.address.startsWith('Location:') && 
      metadata.address !== 'Location Unavailable' &&
      metadata.address.trim().length > 0;
    
    // Get overlay scale from image or use default
    const overlayScale = parseFloat(img.getAttribute('data-overlay-scale') || '1.0');
    
    // Serialize metadata with date handling, visibility, and scale
    const serializedMetadata = {
      latitude: metadata.latitude,
      longitude: metadata.longitude,
      address: metadata.address,
      capturedAt: metadata.capturedAt.toISOString(),
      visible: hasValidMetadata, // Smart default based on metadata quality
      overlayScale: overlayScale // Include scale factor
    };
    container.setAttribute('data-metadata', JSON.stringify(serializedMetadata));
    container.setAttribute('data-has-overlay', 'true');
  } else {
    container.setAttribute('data-has-overlay', 'false');
  }

  // Minimal styling - will be enhanced by CSS float classes
  container.style.cssText = `
    display: inline-block;
    margin: 0 4px 0 0;
    vertical-align: top;
    max-width: 100%;
    position: relative;
  `;

  // Ensure image is also atomic within container
  img.contentEditable = 'false';
  
  // Add image to container
  container.appendChild(img);

  /*console.log('🎯 Created minimal image container for React portals:', {
    overlayId,
    hasMetadata: !!metadata,
    containerHTML: container.outerHTML.substring(0, 100) + '...'
  });*/

  return container;
};

/**
 * Create viewport overlay with PROPER scroll handling - completely outside contentEditable
 */
const createProperViewportOverlay = (img: HTMLImageElement, metadata: ImageMetadata): void => {
  // Clean up any existing overlay for this image first
  const existingOverlay = document.body.querySelector(`.viewport-overlay[data-image-id="${img.getAttribute('data-image-id')}"]`);
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay viewport-overlay';
  overlay.setAttribute('data-image-id', img.getAttribute('data-image-id') || '');
  overlay.setAttribute('data-overlay-type', 'metadata');
  
  // Create the content using the existing HTML generation
  overlay.innerHTML = createOverlayHTML(metadata);
  
  // Append to body (completely outside contentEditable)
  document.body.appendChild(overlay);
  
  // Position update function with CORRECT scroll handling
  const updatePosition = () => {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      overlay.style.opacity = '0';
      return;
    }
    
    const shouldBeVisible = img.dataset.metadataVisible === 'true';
    
    // Calculate position in document coordinates (NOT viewport coordinates)
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Convert viewport coordinates to document coordinates
    const left = rect.left + scrollLeft + 6;
    const top = rect.bottom + scrollTop - 80 - 6; // Assume overlay height ~80px
    
    overlay.style.cssText = `
      position: absolute;
      left: ${Math.max(0, left)}px;
      top: ${Math.max(0, top)}px;
      opacity: ${shouldBeVisible ? '1' : '0'};
      pointer-events: none;
      user-select: none;
      z-index: 1000;
      transition: opacity 0.2s ease;
    `;
  };
  
  // Initial positioning (with delay to ensure layout is complete)
  requestAnimationFrame(() => {
    requestAnimationFrame(updatePosition);
  });
  
  // Update position on scroll AND resize
  let ticking = false;
  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(() => {
        updatePosition();
        ticking = false;
      });
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updatePosition, { passive: true });
  
  // Store cleanup
  (img as any).__overlayCleanup = () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', updatePosition);
    overlay.remove();
  };
  
  //console.log('🖼️ Created proper viewport overlay for image:', img.getAttribute('data-image-id'));
};

/**
 * Create CSS-positioned overlay that stays with image using pure CSS
 * No JavaScript positioning or scroll tracking needed
 */
const createCSSPositionedOverlay = (img: HTMLImageElement, metadata: ImageMetadata, container: HTMLElement): void => {
  // Clean up any existing overlay for this image first
  const existingOverlay = container.querySelector('.image-metadata-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay css-positioned';
  overlay.setAttribute('data-image-id', img.getAttribute('data-image-id') || '');
  overlay.setAttribute('data-overlay-type', 'metadata');
  
  // Create the content using the existing HTML generation
  overlay.innerHTML = createOverlayHTML(metadata);
  
  // Position with pure CSS - completely invisible to contentEditable
  overlay.style.cssText = `
    position: absolute;
    bottom: 6px;
    left: 6px;
    width: 66.666%;
    max-width: 280px;
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
    z-index: 10;
    opacity: ${img.dataset.metadataVisible === 'true' ? '1' : '0'};
    transition: opacity 0.2s ease;
    font-size: 0;
    line-height: 0;
    white-space: nowrap;
    overflow: hidden;
  `;
  
  // Append to container (not body) - this is the key!
  container.appendChild(overlay);
  
  //console.log('🖼️ Created CSS-positioned overlay for image:', img.getAttribute('data-image-id'));
};

/**
 * Create fixed viewport overlay that properly accounts for scroll position
 * Uses position: absolute with correct page coordinates
 */
const createFixedViewportOverlay = (img: HTMLImageElement, metadata: ImageMetadata): void => {
  // Clean up any existing overlay for this image first
  const existingOverlay = document.body.querySelector(`.viewport-overlay[data-image-id="${img.getAttribute('data-image-id')}"]`);
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay viewport-overlay';
  overlay.setAttribute('data-image-id', img.getAttribute('data-image-id') || '');
  overlay.setAttribute('data-overlay-type', 'metadata');
  
  // Create the content using the existing HTML generation
  overlay.innerHTML = createOverlayHTML(metadata);
  
  // Append to body but position correctly
  document.body.appendChild(overlay);
  
  // Set up positioning that updates correctly
  const updatePosition = () => {
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      overlay.style.opacity = '0';
      return;
    }
    
    const shouldBeVisible = img.dataset.metadataVisible === 'true';
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Position overlay in bottom-left corner of image
    const left = rect.left + scrollLeft + 6;
    const top = rect.bottom + scrollTop - 80 - 6; // Assume overlay height ~80px
    
    overlay.style.cssText = `
      position: absolute;
      left: ${Math.max(0, left)}px;
      top: ${Math.max(0, top)}px;
      opacity: ${shouldBeVisible ? '1' : '0'};
      pointer-events: none;
      user-select: none;
      z-index: 1000;
      transition: opacity 0.2s ease;
    `;
  };
  
  // Initial positioning
  requestAnimationFrame(() => {
    requestAnimationFrame(updatePosition);
  });
  
  // Update on window resize AND scroll (this is key!)
  window.addEventListener('resize', updatePosition, { passive: true });
  window.addEventListener('scroll', updatePosition, { passive: true });
  
  // Track image resize
  const resizeObserver = new ResizeObserver(updatePosition);
  resizeObserver.observe(img);
  
  // Store cleanup
  (img as any).__overlayCleanup = () => {
    window.removeEventListener('resize', updatePosition);
    window.removeEventListener('scroll', updatePosition);
    resizeObserver.disconnect();
    overlay.remove();
  };
  
  //console.log('🖼️ Created fixed viewport overlay for image:', img.getAttribute('data-image-id'));
};

/**
 * Create container-positioned overlay that stays with the image
 * Uses proper CSS positioning relative to image container
 */
const createContainerPositionedOverlay = (img: HTMLImageElement, metadata: ImageMetadata, container: HTMLElement): void => {
  // Clean up any existing overlay for this image first
  const existingOverlay = container.querySelector('.image-metadata-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }

  // Container should already have position: relative from creation
  
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay container-overlay';
  overlay.setAttribute('data-image-id', img.getAttribute('data-image-id') || '');
  overlay.setAttribute('data-overlay-type', 'metadata');
  
  // Create the content using the existing HTML generation
  overlay.innerHTML = createOverlayHTML(metadata);
  
  // Position overlay in bottom-left corner with CSS
  overlay.style.cssText = `
    position: absolute;
    bottom: 6px;
    left: 6px;
    width: 66.666%;
    pointer-events: none;
    user-select: none;
    z-index: 1000;
    opacity: ${img.dataset.metadataVisible === 'true' ? '1' : '0'};
    transition: opacity 0.2s ease;
  `;
  
  // Append to image container instead of body
  container.appendChild(overlay);
  
  //console.log('🖼️ Created container overlay for image:', img.getAttribute('data-image-id'));
};

/**
 * Create viewport-positioned overlay that exists outside contentEditable flow
 * Completely invisible to contentEditable - no phantom lines!
 */
const createViewportPositionedOverlay = (img: HTMLImageElement, metadata: ImageMetadata): void => {
  // Clean up any existing overlay for this image first
  const existingOverlay = document.body.querySelector(`.viewport-overlay[data-image-id="${img.getAttribute('data-image-id')}"]`);
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay viewport-overlay';
  overlay.setAttribute('data-image-id', img.getAttribute('data-image-id') || '');
  overlay.setAttribute('data-overlay-type', 'metadata');
  
  // Create the content first
  const addressComponents = parseAddress(metadata.address || '');
  
  // Create map tile
  const mapTile = document.createElement('div');
  mapTile.className = 'metadata-map-tile';
  
  if (metadata.latitude && metadata.longitude) {
    const mapImg = document.createElement('img');
    mapImg.src = getMapTileURL(metadata.latitude, metadata.longitude);
    mapImg.alt = 'Map location';
    mapImg.style.cssText = 'width: 60px; height: 60px; object-fit: cover; border-radius: 4px;';
    
    const pin = document.createElement('div');
    pin.className = 'metadata-location-pin';
    
    mapTile.appendChild(mapImg);
    mapTile.appendChild(pin);
  }
  
  // Create text info with iOS format: City, Street, Zip, Timestamp
  const textInfo = document.createElement('div');
  textInfo.className = 'metadata-text-info';
  
  // Line 1: City, State (or Country)
  if (addressComponents.cityState) {
    const cityState = document.createElement('div');
    cityState.className = 'metadata-city-state';
    cityState.textContent = addressComponents.cityState;
    textInfo.appendChild(cityState);
  }
  
  // Line 2: Street address (house number + street name)
  if (addressComponents.street) {
    const street = document.createElement('div');
    street.className = 'metadata-street';
    street.textContent = addressComponents.street;
    textInfo.appendChild(street);
  }
  
  // Line 3: Zip code
  if (addressComponents.zip) {
    const zip = document.createElement('div');
    zip.className = 'metadata-zip';
    zip.textContent = addressComponents.zip;
    textInfo.appendChild(zip);
  }
  
  // Line 4: Timestamp with "at" format (Mar 18, 2025 at 10:23 AM)
  if (metadata.capturedAt) {
    const timestamp = document.createElement('div');
    timestamp.className = 'metadata-timestamp';
    timestamp.textContent = formatTimestampWithAt(metadata.capturedAt);
    textInfo.appendChild(timestamp);
  }
  
  // Assemble overlay
  overlay.appendChild(mapTile);
  overlay.appendChild(textInfo);
  
  // Add to document body first
  document.body.appendChild(overlay);
  
  // Force initial positioning after DOM renders and measures dimensions
  // Use double requestAnimationFrame to ensure layout is complete
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const isVisible = img.dataset.metadataVisible === 'true';
      overlay.style.opacity = isVisible ? '1' : '0';
      
      // Position overlay relative to image
      updateViewportOverlayPosition(img, overlay);
    });
  });
  
  // Set up position tracking (only for window resize and image resize, NOT scroll)
  const updatePosition = () => updateViewportOverlayPosition(img, overlay);
  
  // Only track window resize to keep overlay positioned (NO scroll listener!)
  window.addEventListener('resize', updatePosition, { passive: true });
  
  // Track image resize using ResizeObserver
  const resizeObserver = new ResizeObserver(() => {
    updatePosition();
  });
  resizeObserver.observe(img);
  
  // Store cleanup function on image
  (img as any).__overlayCleanup = () => {
    window.removeEventListener('resize', updatePosition);
    resizeObserver.disconnect();
    overlay.remove();
  };
  
  //console.log('🖼️ Created viewport overlay for image:', img.getAttribute('data-image-id'));
};

/**
 * Update viewport overlay position to match image location
 */
const updateViewportOverlayPosition = (img: HTMLImageElement, overlay: HTMLElement): void => {
  try {
    const rect = img.getBoundingClientRect();
    
    if (rect.width === 0 || rect.height === 0) {
      // Image not visible, hide overlay
      overlay.style.opacity = '0';
      return;
    }
    
    // Show overlay if metadata should be visible
    const shouldBeVisible = img.dataset.metadataVisible === 'true';
    
    // Temporarily reset transform to get natural dimensions
    const originalTransform = overlay.style.transform;
    overlay.style.transform = 'none';
    
    // Get actual overlay dimensions
    const overlayRect = overlay.getBoundingClientRect();
    const overlayWidth = overlayRect.width || 200; // Natural width
    const overlayHeight = overlayRect.height || 80; // Natural height
    
    // Calculate scale factor based on image size
    const scale = Math.min(rect.width / 400, 1); // Scale down for smaller images
    
    // Position overlay in bottom-left corner of image with padding
    // Account for the scale transform by calculating the scaled dimensions
    const scaledWidth = overlayWidth * scale;
    const scaledHeight = overlayHeight * scale;
    
    const leftPadding = 6; // Less padding from left edge (move more to the left)
    const bottomPadding = 6; // Less padding from bottom edge (move lower)
    
    // Use page coordinates (including scroll) instead of viewport coordinates
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    const left = rect.left + scrollLeft + leftPadding;
    const top = rect.bottom + scrollTop - scaledHeight - bottomPadding;
    
    // Apply all styles
    overlay.style.position = 'absolute';
    overlay.style.left = Math.max(0, left) + 'px'; // Prevent going off-screen
    overlay.style.top = Math.max(0, top) + 'px';
    overlay.style.transform = `scale(${scale})`;
    overlay.style.transformOrigin = 'top left'; // Changed to top left for easier calculation
    overlay.style.pointerEvents = 'none';
    overlay.style.userSelect = 'none';
    overlay.style.zIndex = '1000';
    overlay.style.opacity = shouldBeVisible ? '1' : '0';
    overlay.style.transition = 'opacity 0.2s ease';
    
    //console.log(`📍 Positioned overlay for image ${img.getAttribute('data-image-id')}: left=${Math.round(left)}, top=${Math.round(top)}, scale=${scale.toFixed(2)}`);
    
  } catch (error) {
    console.error('Error updating overlay position:', error);
  }
};

/**
 * Update all viewport overlays - used when any image is resized and may push other images
 */
const updateAllViewportOverlays = (): void => {
  // Find all viewport overlays
  const viewportOverlays = document.body.querySelectorAll('.viewport-overlay[data-image-id]');
  
  viewportOverlays.forEach(overlay => {
    const imageId = overlay.getAttribute('data-image-id');
    if (!imageId) return;
    
    // Find the corresponding image
    const img = document.querySelector(`.inline-image[data-image-id="${imageId}"]`) as HTMLImageElement;
    if (!img) {
      // If image doesn't exist anymore, remove orphaned overlay
      overlay.remove();
      return;
    }
    
    // Update the overlay position
    updateViewportOverlayPosition(img, overlay as HTMLElement);
  });
};

/**
 * Create overlay HTML content
 */
const createOverlayHTML = (metadata: ImageMetadata): string => {
  const addressComponents = parseAddress(metadata.address || '');
  
  let mapTileHTML = '';
  if (metadata.latitude && metadata.longitude) {
    const mapUrl = getMapTileURL(metadata.latitude, metadata.longitude);
    mapTileHTML = `
      <div class="metadata-map-tile">
        <img src="${mapUrl}" alt="Map location" style="width: 100%; height: 100%; object-fit: cover;">
        <div class="metadata-location-pin"></div>
      </div>
    `;
  } else {
    mapTileHTML = `<div class="metadata-map-tile"></div>`;
  }
  
  const timestamp = metadata.capturedAt ? formatTimestamp(metadata.capturedAt) : '';
  
  // Format timestamp with "at" for consistency
  const timestampWithAt = metadata.capturedAt ? formatTimestampWithAt(metadata.capturedAt) : '';
  
  return `
    ${mapTileHTML}
    <div class="metadata-text-info">
      ${addressComponents.cityState ? `<div class="metadata-city-state">${addressComponents.cityState}</div>` : ''}
      ${addressComponents.street ? `<div class="metadata-street">${addressComponents.street}</div>` : ''}
      ${addressComponents.zip ? `<div class="metadata-zip">${addressComponents.zip}</div>` : ''}
      ${timestampWithAt ? `<div class="metadata-timestamp">${timestampWithAt}</div>` : ''}
    </div>
  `;
};

/**
 * Create DOM-anchored metadata overlay - LEGACY VERSION
 * Designed to survive pagination rebuilds via rebindAllImageOverlays()
 */
export const createMetadataOverlay = (metadata: ImageMetadata): HTMLElement => {
  const overlay = document.createElement('div');
  overlay.className = 'image-metadata-overlay';
  // DON'T set contentEditable="false" on overlay - causes range issues
  overlay.setAttribute('data-overlay-type', 'metadata');
  overlay.setAttribute('data-non-editable', 'true');
  // Keep overlay non-interactive (CSS handles this too)
  overlay.style.pointerEvents = 'none';
  overlay.style.userSelect = 'none';
  
  // Create mini map container
  const mapTile = document.createElement('div');
  mapTile.className = 'metadata-map-tile';
  
  // Add map image if coordinates are available
  if (metadata.latitude && metadata.longitude) {
    const mapImg = document.createElement('img');
    mapImg.src = getMapTileURL(metadata.latitude, metadata.longitude);
    mapImg.alt = 'Map location';
    mapImg.crossOrigin = 'anonymous'; // PDF export compatibility
    mapTile.appendChild(mapImg);
    
    // Add location pin
    const pin = document.createElement('div');
    pin.className = 'metadata-location-pin';
    mapTile.appendChild(pin);
  }
  
  // Create text information container
  const textInfo = document.createElement('div');
  textInfo.className = 'metadata-text-info';
  
  // CRITICAL: Only create text content if we have a REAL street address
  // This function should only be called after smart address upgrade has been attempted
  if (metadata.address && 
      metadata.address.trim() !== '' && 
      !metadata.address.startsWith('Location: ')) {
    
    // Parse actual address
    const addressComponents = parseAddress(metadata.address);
    
    // Only create overlay elements if we have meaningful address components
    if (addressComponents.cityState && addressComponents.cityState !== '') {
      // City/State (primary text)
      const cityState = document.createElement('div');
      cityState.className = 'metadata-city-state';
      cityState.textContent = addressComponents.cityState;
      textInfo.appendChild(cityState);
      
      // Street address (secondary text)
      if (addressComponents.street) {
        const street = document.createElement('div');
        street.className = 'metadata-street';
        street.textContent = addressComponents.street;
        textInfo.appendChild(street);
      }
      
      // ZIP code (tertiary text)
      if (addressComponents.zip) {
        const zip = document.createElement('div');
        zip.className = 'metadata-zip';
        zip.textContent = addressComponents.zip;
        textInfo.appendChild(zip);
      }
    }
  } else {
    // Fallback for images without any location data
    // NOTE: This should rarely be reached now that we have smart upgrade
    const locationUnavailable = document.createElement('div');
    locationUnavailable.className = 'metadata-city-state';
    locationUnavailable.textContent = 'Location Unavailable';
    textInfo.appendChild(locationUnavailable);
    
    const noGpsData = document.createElement('div');
    noGpsData.className = 'metadata-street';
    noGpsData.textContent = 'No GPS data';
    textInfo.appendChild(noGpsData);
  }
  
  // Timestamp - always present
  const timestamp = document.createElement('div');
  timestamp.className = 'metadata-timestamp';
  timestamp.textContent = formatTimestamp(metadata.capturedAt);
  textInfo.appendChild(timestamp);
  
  // Assemble overlay - NO positioning logic (handled by CSS)
  overlay.appendChild(mapTile);
  overlay.appendChild(textInfo);
  
  return overlay;
};

/**
 * Parse address string into components (matching Swift implementation)
 */
const parseAddress = (address: string): AddressComponents => {
  // Skip coordinate-style addresses - they should not create overlays
  if (address.startsWith('Location: ')) {
    return {
      street: '',
      cityState: '',
      zip: ''
    };
  }
  
  // Split address by commas
  const components = address.split(',').map(c => c.trim());
  
  let street = '';
  let cityState = '';
  let zip = '';
  
  if (components.length >= 3) {
    // Standard format: "123 Main St, City, State 12345"
    street = components[0];
    
    // Look for zip code (5 digits, possibly with +4)
    for (let i = 1; i < components.length; i++) {
      if (/^\d{5}(-\d{4})?$/.test(components[i])) {
        zip = components[i];
        // City and state are everything before the zip
        cityState = components.slice(1, i).join(', ');
        break;
      }
    }
    
    // If no zip found, assume everything after street is city/state
    if (!zip) {
      cityState = components.slice(1).join(', ');
    }
  } else if (components.length === 2) {
    // Two parts: might be "Street, City" or "City, State"
    if (/\d/.test(components[0])) {
      // First has numbers, likely street
      street = components[0];
      cityState = components[1];
    } else {
      // No numbers, likely "City, State"
      cityState = address;
    }
  } else {
    // Single component
    if (/\d/.test(address)) {
      street = address;
      cityState = 'Unknown';
    } else {
      cityState = address;
    }
  }
  
  return {
    street,
    cityState: cityState || 'Unknown Location',
    zip
  };
};

/**
 * Generate OpenStreetMap tile URL (matching Swift implementation)
 */
const getMapTileURL = (lat: number, lon: number, zoom: number = 16): string => {
  // Calculate tile coordinates using Web Mercator projection
  const tileX = Math.floor((lon + 180.0) / 360.0 * Math.pow(2.0, zoom));
  const tileY = Math.floor((1.0 - Math.log(Math.tan(lat * Math.PI / 180.0) + 1.0 / Math.cos(lat * Math.PI / 180.0)) / Math.PI) / 2.0 * Math.pow(2.0, zoom));
  
  return `https://tile.openstreetmap.org/${zoom}/${tileX}/${tileY}.png`;
};

/**
 * Get city name from coordinates (matching Swift implementation)
 */
const getCityFromCoordinates = (lat: number, lon: number): string => {
  // Austin area coordinates (rough approximation)
  if (lat >= 30.0 && lat <= 30.6 && lon >= -97.9 && lon <= -97.5) {
    return 'Austin, TX';
  }
  return 'Current Location';
};

/**
 * Format timestamp to match Swift version
 */
const formatTimestamp = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  return formatter.format(date);
};

/**
 * Format timestamp with "at" to match iOS overlay format
 * Example: "Mar 18, 2025 at 10:23 AM"
 */
const formatTimestampWithAt = (date: Date): string => {
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
  
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  
  const dateStr = dateFormatter.format(date);
  const timeStr = timeFormatter.format(date);
  
  return `${dateStr} at ${timeStr}`;
};

/**
 * Fetch real street address from coordinates (matching iOS smart upgrade)
 * Uses OpenStreetMap Nominatim service for reverse geocoding
 */
const fetchAddressFromCoordinates = async (lat: number, lon: number): Promise<string | null> => {
  try {
    // Use OpenStreetMap Nominatim service (free, no API key required)
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'User-Agent': 'PropertyReporter/1.0 (Web Client)'
      }
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    
    if (!data || !data.address) {
      return null;
    }
    
    // Extract address components (matching iOS format)
    const address = data.address;
    const houseNumber = address.house_number || '';
    const street = address.road || address.street || '';
    const city = address.city || address.town || address.village || '';
    const state = address.state || '';
    const postcode = address.postcode || '';
    
    // Build formatted address string (matching iOS format)
    let formattedAddress = '';
    
    if (houseNumber && street) {
      formattedAddress = `${houseNumber} ${street}`;
    } else if (street) {
      formattedAddress = street;
    }
    
    if (city && state) {
      if (formattedAddress) {
        formattedAddress += `, ${city}, ${state}`;
      } else {
        formattedAddress = `${city}, ${state}`;
      }
    } else if (city) {
      if (formattedAddress) {
        formattedAddress += `, ${city}`;
      } else {
        formattedAddress = city;
      }
    }
    
    if (postcode) {
      formattedAddress += ` ${postcode}`;
    }
    
    // Only return if we have a meaningful address (not just coordinates)
    if (formattedAddress && formattedAddress.trim() !== '') {
      return formattedAddress;
    } else {
      return null;
    }
    
  } catch (error) {
    console.error('Address fetch error:', error);
    return null;
  }
};

/**
 * Adjust overlay scale for an image
 * @param img - The image element
 * @param delta - The scale adjustment (-0.1 or +0.1)
 */
const adjustOverlayScale = (img: HTMLImageElement, delta: number): void => {
  // Get current scale (default is 1.0)
  const currentScale = parseFloat(img.getAttribute('data-overlay-scale') || '1.0');
  
  // Calculate new scale (min: 0.5, max: 2.0)
  let newScale = currentScale + delta;
  newScale = Math.max(0.5, Math.min(2.0, newScale));
  newScale = Math.round(newScale * 10) / 10; // Round to 1 decimal place
  
  // Update image data attribute
  img.setAttribute('data-overlay-scale', newScale.toString());
  
  // Update container's data-metadata JSON
  const container = img.closest('.inline-image-container') as HTMLElement;
  if (container) {
    const metadataStr = container.getAttribute('data-metadata');
    if (metadataStr) {
      try {
        const metadata = JSON.parse(metadataStr);
        metadata.overlayScale = newScale;
        container.setAttribute('data-metadata', JSON.stringify(metadata));
      } catch (error) {
        console.error('Failed to update overlay scale in metadata:', error);
      }
    }
  }
  
  // Dispatch custom event to trigger overlay position re-extraction with new scale
  const imageId = img.getAttribute('data-image-id');
  document.dispatchEvent(new CustomEvent('metadataScaleChanged', { 
    detail: { imageId, scale: newScale } 
  }));
};

/**
 * Handle right-click context menu for images
 */
const handleImageContextMenu = (event: MouseEvent): void => {
  event.preventDefault();
  event.stopPropagation();
  
  const img = event.target as HTMLImageElement;
  
  // Remove any existing context menus
  document.querySelectorAll('.image-context-menu').forEach(menu => menu.remove());
  
  // Check if image has metadata
  const hasMetadata = img.getAttribute('data-metadata-captured');
  
  // Create context menu
  const menu = document.createElement('div');
  menu.className = 'image-context-menu';
  menu.style.cssText = `
    position: fixed;
    left: ${event.clientX}px;
    top: ${event.clientY}px;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    z-index: 10000;
    min-width: 180px;
    padding: 4px 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 14px;
  `;
  
  if (hasMetadata) {
    // Check current visibility state
    const isVisible = img.getAttribute('data-metadata-visible') === 'true';
    
    // Create show/hide metadata menu item
    const menuItem = document.createElement('button');
    menuItem.className = 'image-context-menu-item';
    menuItem.textContent = isVisible ? 'Hide Metadata' : 'Show Metadata';
    menuItem.style.cssText = `
      width: 100%;
      padding: 8px 16px;
      border: none;
      background: white;
      text-align: left;
      cursor: pointer;
      font-size: 14px;
      color: #333;
      display: block;
    `;
    
    menuItem.addEventListener('mouseenter', () => {
      menuItem.style.background = '#e8f4f8';
    });
    
    menuItem.addEventListener('mouseleave', () => {
      menuItem.style.background = 'white';
    });
    
    menuItem.addEventListener('click', () => {
      toggleMetadataOverlay(img);
      menu.remove();
    });
    
    menu.appendChild(menuItem);
    
    // Add separator
    const separator = document.createElement('div');
    separator.style.cssText = `
      height: 1px;
      background: #e0e0e0;
      margin: 4px 0;
    `;
    menu.appendChild(separator);
    
    // Create overlay size control section
    const sizeControl = document.createElement('div');
    sizeControl.className = 'image-context-menu-item';
    sizeControl.style.cssText = `
      width: 100%;
      padding: 8px 16px;
      background: white;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      color: #333;
    `;
    
    const sizeLabel = document.createElement('span');
    sizeLabel.textContent = 'Overlay Size:';
    sizeLabel.style.cssText = `
      font-size: 14px;
      color: #333;
    `;
    
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 8px;
    `;
    
    // Get current overlay scale (default is 1.0)
    const currentScale = parseFloat(img.getAttribute('data-overlay-scale') || '1.0');
    
    // Create minus button
    const minusButton = document.createElement('button');
    minusButton.textContent = '−';
    minusButton.style.cssText = `
      width: 28px;
      height: 28px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: white;
      cursor: pointer;
      font-size: 18px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333;
      transition: background 0.2s;
    `;
    minusButton.disabled = currentScale <= 0.5;
    if (minusButton.disabled) {
      minusButton.style.opacity = '0.4';
      minusButton.style.cursor = 'not-allowed';
    }
    
    minusButton.addEventListener('mouseenter', () => {
      if (!minusButton.disabled) {
        minusButton.style.background = '#f0f0f0';
      }
    });
    
    minusButton.addEventListener('mouseleave', () => {
      minusButton.style.background = 'white';
    });
    
    minusButton.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!minusButton.disabled) {
        adjustOverlayScale(img, -0.1);
        menu.remove();
      }
    });
    
    // Create plus button
    const plusButton = document.createElement('button');
    plusButton.textContent = '+';
    plusButton.style.cssText = `
      width: 28px;
      height: 28px;
      border: 1px solid #ccc;
      border-radius: 4px;
      background: white;
      cursor: pointer;
      font-size: 18px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333;
      transition: background 0.2s;
    `;
    plusButton.disabled = currentScale >= 2.0;
    if (plusButton.disabled) {
      plusButton.style.opacity = '0.4';
      plusButton.style.cursor = 'not-allowed';
    }
    
    plusButton.addEventListener('mouseenter', () => {
      if (!plusButton.disabled) {
        plusButton.style.background = '#f0f0f0';
      }
    });
    
    plusButton.addEventListener('mouseleave', () => {
      plusButton.style.background = 'white';
    });
    
    plusButton.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!plusButton.disabled) {
        adjustOverlayScale(img, 0.1);
        menu.remove();
      }
    });
    
    buttonContainer.appendChild(minusButton);
    buttonContainer.appendChild(plusButton);
    
    sizeControl.appendChild(sizeLabel);
    sizeControl.appendChild(buttonContainer);
    
    menu.appendChild(sizeControl);
    
    // Add separator
    const separator2 = document.createElement('div');
    separator2.style.cssText = `
      height: 1px;
      background: #e0e0e0;
      margin: 4px 0;
    `;
    menu.appendChild(separator2);
    
    // Add alignment section
    const alignmentSection = document.createElement('div');
    alignmentSection.style.cssText = `
      padding: 4px 0;
    `;
    
    const alignmentLabel = document.createElement('div');
    alignmentLabel.textContent = 'Image Alignment';
    alignmentLabel.style.cssText = `
      padding: 6px 16px;
      font-size: 12px;
      color: #666;
      font-weight: 500;
    `;
    alignmentSection.appendChild(alignmentLabel);
    
    // Get current alignment
    const container = img.closest('.inline-image-container') as HTMLElement;
    const currentAlignment = getImageAlignment(img);
    
    // Alignment options
    const alignmentOptions = [
      { value: 'left', label: 'Float Left', icon: '⬅' },
      { value: 'right', label: 'Float Right', icon: '➡' },
      { value: 'center', label: 'Center', icon: '⬌' },
      { value: 'inline', label: 'Inline', icon: '↔' }
    ];
    
    alignmentOptions.forEach(option => {
      const alignButton = document.createElement('button');
      alignButton.className = 'image-context-menu-item';
      alignButton.style.cssText = `
        width: 100%;
        padding: 8px 16px;
        border: none;
        background: ${currentAlignment === option.value ? '#e8f4f8' : 'white'};
        text-align: left;
        cursor: pointer;
        font-size: 14px;
        color: #333;
        display: flex;
        align-items: center;
        gap: 8px;
      `;
      
      const icon = document.createElement('span');
      icon.textContent = option.icon;
      icon.style.cssText = 'font-size: 16px;';
      
      const label = document.createElement('span');
      label.textContent = option.label;
      
      if (currentAlignment === option.value) {
        const checkmark = document.createElement('span');
        checkmark.textContent = '✓';
        checkmark.style.cssText = 'margin-left: auto; color: #2196F3; font-weight: bold;';
        alignButton.appendChild(icon);
        alignButton.appendChild(label);
        alignButton.appendChild(checkmark);
      } else {
        alignButton.appendChild(icon);
        alignButton.appendChild(label);
      }
      
      alignButton.addEventListener('mouseenter', () => {
        if (currentAlignment !== option.value) {
          alignButton.style.background = '#f5f5f5';
        }
      });
      
      alignButton.addEventListener('mouseleave', () => {
        alignButton.style.background = currentAlignment === option.value ? '#e8f4f8' : 'white';
      });
      
      alignButton.addEventListener('click', () => {
        setImageAlignment(img, option.value as 'left' | 'right' | 'center' | 'inline');
        menu.remove();
      });
      
      alignmentSection.appendChild(alignButton);
    });
    
    menu.appendChild(alignmentSection);
  } else {
    // Create disabled menu item for images without metadata
    const menuItem = document.createElement('div');
    menuItem.className = 'image-context-menu-item';
    menuItem.textContent = 'No Metadata Available';
    menuItem.style.cssText = `
      width: 100%;
      padding: 8px 16px;
      background: white;
      text-align: left;
      font-size: 14px;
      color: #999;
      cursor: default;
    `;
    
    menu.appendChild(menuItem);
  }
  
  document.body.appendChild(menu);
  
  // Remove menu when clicking elsewhere
  const removeMenu = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node)) {
      menu.remove();
      document.removeEventListener('click', removeMenu);
    }
  };
  
  setTimeout(() => {
    document.addEventListener('click', removeMenu);
  }, 10);
};

/**
 * Set up right-click context menu for image metadata toggle
 */
export const setupImageContextMenu = (img: HTMLImageElement): void => {
  // Remove any existing listeners
  img.removeEventListener('contextmenu', handleImageContextMenu);
  
  // Add right-click context menu
  img.addEventListener('contextmenu', handleImageContextMenu);
};

/**
 * Toggle metadata overlay visibility for an image (React Portal system)
 */
export const toggleMetadataOverlay = (img: HTMLImageElement): void => {
  const isVisible = img.getAttribute('data-metadata-visible') === 'true';
  const newVisibility = !isVisible;
  
  // Update data attribute on image
  img.setAttribute('data-metadata-visible', newVisibility.toString());
  
  // Update container's data-metadata JSON
  const container = img.closest('.inline-image-container') as HTMLElement;
  if (container) {
    const metadataStr = container.getAttribute('data-metadata');
    if (metadataStr) {
      try {
        const metadata = JSON.parse(metadataStr);
        metadata.visible = newVisibility;
        container.setAttribute('data-metadata', JSON.stringify(metadata));
      } catch (error) {
        console.error('Failed to update metadata visibility:', error);
      }
    }
  }
  
  // Dispatch custom event to trigger overlay position re-extraction
  const imageId = img.getAttribute('data-image-id');
  document.dispatchEvent(new CustomEvent('metadataVisibilityChanged', { 
    detail: { imageId, visible: newVisibility } 
  }));
};

/**
 * CRITICAL: Rebind all image overlays after pagination rebuilds DOM
 * This function MUST be called after every innerHTML replacement
 * 
 * Scans all .inline-image-container elements and ensures each has exactly one overlay
 * Recreates missing overlays from dataset metadata stored on <img> elements
 * NEVER creates duplicates - always removes existing overlays first
 */
export const rebindAllImageOverlays = async (root: HTMLElement): Promise<void> => {
  // CRITICAL: Clean up ALL existing overlays (both container-anchored and viewport)
  const rootOverlays = root.querySelectorAll('.image-metadata-overlay');
  rootOverlays.forEach(overlay => overlay.remove());
  
  // Clean up viewport overlays from document body
  const viewportOverlays = document.body.querySelectorAll('.image-metadata-overlay.viewport-overlay');
  viewportOverlays.forEach(overlay => overlay.remove());
  
  // Find all images directly (no containers anymore)
  const images = root.querySelectorAll('.inline-image');
  
  // Process images sequentially to avoid API rate limiting
  for (let i = 0; i < images.length; i++) {
    const img = images[i] as HTMLImageElement;
    
    // Reconstruct metadata from dataset
    const metadata: ImageMetadata = {
      latitude: img.dataset.metadataLat ? parseFloat(img.dataset.metadataLat) : undefined,
      longitude: img.dataset.metadataLng ? parseFloat(img.dataset.metadataLng) : undefined,
      address: img.dataset.metadataAddress || undefined,
      capturedAt: img.dataset.metadataCaptured ? new Date(img.dataset.metadataCaptured) : new Date()
    };
    
    // 🎯 Ensure data-metadata-visible attribute exists (for backward compatibility)
    if (!img.hasAttribute('data-metadata-visible') && img.dataset.metadataCaptured) {
      const hasValidMetadata = metadata.address && 
        !metadata.address.startsWith('Location:') && 
        metadata.address !== 'Location Unavailable' &&
        metadata.address.trim().length > 0;
      img.setAttribute('data-metadata-visible', hasValidMetadata ? 'true' : 'false');
    }
    
    // OLD HTML overlays disabled - using React Portal system instead
    // createProperViewportOverlay(img, metadata);
    
    // Reinitialize double-click handler
    setupImageContextMenu(img);
  }
};

/**
 * Debug logger for image overlay system - production version (minimal logging)
 * Only logs critical issues and errors
 */
export const debugImageOverlays = (root: HTMLElement): void => {
  const containers = root.querySelectorAll('.inline-image-container');
  const images = root.querySelectorAll('.inline-image');
  const overlays = root.querySelectorAll('.image-metadata-overlay');
  
  // Check for orphaned overlays (not inside containers)
  const orphanedOverlays = Array.from(overlays).filter(overlay => 
    !overlay.closest('.inline-image-container')
  );
  if (orphanedOverlays.length > 0) {
    console.warn(`Found ${orphanedOverlays.length} orphaned overlays outside containers`);
  }
  
  // Check each image for critical issues only
  images.forEach((img, index) => {
    if (!(img instanceof HTMLImageElement)) return;
    
    const container = img.closest('.inline-image-container') as HTMLElement;
    const overlay = container?.querySelector('.image-metadata-overlay') as HTMLElement;
    
    const hasContainer = !!container;
    const hasOverlay = !!overlay;
    const overlayInCorrectParent = hasOverlay && overlay.parentElement === container;
    
    // Get metadata availability
    const hasMetadata = !!(
      img.dataset.metadataLat || 
      img.dataset.metadataLng || 
      img.dataset.metadataCaptured
    );
    
    // Only log critical issues
    if (!hasContainer) {
      console.warn(`Image ${index + 1}: Missing container wrapper`);
    }
    if (hasMetadata && !hasOverlay && img.dataset.metadataVisible === 'true') {
      console.warn(`Image ${index + 1}: Has metadata but missing overlay`);
    }
    if (hasOverlay && !overlayInCorrectParent) {
      console.warn(`Image ${index + 1}: Overlay attached to wrong parent`);
    }
  });
};

/**
 * Initialize context menus for all existing images in a container
 */
export const initializeAllImageContextMenus = (container: HTMLElement): void => {
  const images = container.querySelectorAll('.inline-image');
  
  images.forEach((img) => {
    if (img instanceof HTMLImageElement) {
      setupImageContextMenu(img);
    }
  });
  
  // NOTE: No longer calling rebindAllImageOverlays here to prevent duplicates
  // The caller should handle overlay rebinding explicitly before this function
};

/**
 * Set image float alignment (Google Docs-style text wrapping)
 * @param img - The image element to modify
 * @param alignment - 'left' | 'right' | 'center' | 'inline'
 */
export const setImageAlignment = (img: HTMLImageElement, alignment: 'left' | 'right' | 'center' | 'inline'): void => {
  const container = img.closest('.inline-image-container') as HTMLElement;
  if (!container) {
    console.warn('Cannot set alignment: image not in container');
    return;
  }
  
  // Remove all existing alignment classes
  container.classList.remove('image-float-left', 'image-float-right', 'image-center');
  
  // Add new alignment class
  switch (alignment) {
    case 'left':
      container.classList.add('image-float-left');
      break;
    case 'right':
      container.classList.add('image-float-right');
      break;
    case 'center':
      container.classList.add('image-center');
      break;
    case 'inline':
      // No class needed - default inline-block behavior
      break;
  }
  
  // Update resize handles if image is selected
  if (img.classList.contains('selected')) {
    setTimeout(() => updateHandlePositions(img), 50);
  }
};

/**
 * Get current image alignment
 */
export const getImageAlignment = (img: HTMLImageElement): 'left' | 'right' | 'center' | 'inline' => {
  const container = img.closest('.inline-image-container') as HTMLElement;
  if (!container) return 'inline';
  
  if (container.classList.contains('image-float-left')) return 'left';
  if (container.classList.contains('image-float-right')) return 'right';
  if (container.classList.contains('image-center')) return 'center';
  return 'inline';
};
